import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { usePropertyContacts, usePropertyOrganizations, usePropertyClaims } from "@/hooks/use-properties";
import { LinkEntityPopover, LinkedEntityRow } from "../LinkEntityPopover";
import type { LinkableEntity } from "../LinkEntityPopover";
import { navigateDirectory } from "../navigate";
import { apiClient } from "@/lib/api-client";
import { Building2, MapPin, User, FileText, StickyNote } from "lucide-react";
import { useLocation } from "wouter";
import type { Property } from "@/api/generated/client/models";

interface PropertyDrawerProps {
    property: Property | null;
    open: boolean;
    onClose: () => void;
    onEdit: (property: Property) => void;
}

export function PropertyDrawer({ property, open, onClose, onEdit }: PropertyDrawerProps) {
    const [, setLocation] = useLocation();
    const { contacts, isLoading, linkContact, unlinkContact } = usePropertyContacts(property?.id ?? null);
    const { organizations, isLoading: orgsLoading, linkOrganization, unlinkOrganization } = usePropertyOrganizations(property?.id ?? null);
    const { propertyClaims, isLoading: claimsLoading, linkClaim, unlinkClaim } = usePropertyClaims(property?.id ?? null);

    const searchContacts = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.contacts.listContacts({ search: query || undefined, limit: 15 });
        return (res.contacts || []).map((c) => ({ id: c.id, label: `${c.firstName} ${c.lastName}`, badge: c.contactType }));
    }, []);

    const searchOrganizations = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.organizations.listOrganizations({ search: query || undefined, limit: 15 });
        return (res.organizations || []).map((o) => ({ id: o.id, label: o.name || `Org #${o.id}`, badge: o.orgType }));
    }, []);

    const searchClaims = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.claims.listClaims({ search: query || undefined, limit: 15 });
        return (res.claims || [])
            // Only allow linking claims the user can edit; read-only shares are hidden.
            .filter((c) => (c.canWrite ?? false))
            .map((c) => ({
                id: c.id,
                label: [c.insuredName, c.claimNumber].filter(Boolean).join(" – ") || `Claim #${c.id}`,
            }));
    }, []);

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="overflow-y-auto overflow-x-hidden sm:max-w-lg">
                {property && (
                    <>
                        <SheetHeader className="pb-4">
                            <SheetTitle className="text-xl flex items-center gap-2 break-words min-w-0">
                                <MapPin className="h-5 w-5 text-muted-foreground shrink-0" />
                                <span className="break-words min-w-0">
                                    {[property.streetAddress, property.unit, property.city, property.state, property.zipCode].filter(Boolean).join(", ")}
                                </span>
                            </SheetTitle>
                        </SheetHeader>

                        {/* Property Details */}
                        <div className="space-y-3 pb-4">
                            {property.notes && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <StickyNote className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="break-words min-w-0">{property.notes}</span>
                                </div>
                            )}
                        </div>

                        <Button variant="outline" size="sm" onClick={() => onEdit(property)}>
                            Edit Property
                        </Button>

                        <Separator className="my-6" />

                        {/* Linked Contacts */}
                        <div>
                            <div className="flex items-center justify-between flex-wrap mb-3">
                                <h4 className="text-sm font-semibold flex items-center gap-2">
                                    <User className="h-4 w-4" /> Contacts ({contacts.length})
                                </h4>
                                <LinkEntityPopover
                                    existingIds={contacts.map((c) => c.id)}
                                    onSearch={searchContacts}
                                    onSelect={(id) => linkContact(id)}
                                    placeholder="Search contacts..."
                                    emptyMessage="No matching contacts"
                                    noItemsMessage="No contacts yet"
                                    icon={User}
                                />
                            </div>
                            {isLoading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-8 w-full" />
                                    <Skeleton className="h-8 w-full" />
                                </div>
                            ) : contacts.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No contacts linked to this property.</p>
                            ) : (
                                <div className="space-y-2">
                                    {contacts.map((c) => (
                                        <LinkedEntityRow
                                            key={c.id}
                                            icon={User}
                                            label={`${c.firstName} ${c.lastName}`}
                                            onNavigate={() => { onClose(); navigateDirectory("contacts", c.id); }}
                                            onUnlink={() => unlinkContact(c.id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <Separator className="my-6" />

                        {/* Linked Organizations */}
                        <div>
                            <div className="flex items-center justify-between flex-wrap mb-3">
                                <h4 className="text-sm font-semibold flex items-center gap-2">
                                    <Building2 className="h-4 w-4" /> Organizations ({organizations.length})
                                </h4>
                                <LinkEntityPopover
                                    existingIds={organizations.map((o) => o.id)}
                                    onSearch={searchOrganizations}
                                    onSelect={(id) => linkOrganization(id)}
                                    placeholder="Search organizations..."
                                    emptyMessage="No matching organizations"
                                    noItemsMessage="No organizations yet"
                                    icon={Building2}
                                />
                            </div>
                            {orgsLoading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-8 w-full" />
                                </div>
                            ) : organizations.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No organizations linked to this property.</p>
                            ) : (
                                <div className="space-y-2">
                                    {organizations.map((o) => (
                                        <LinkedEntityRow
                                            key={o.id}
                                            icon={Building2}
                                            label={o.name}
                                            onNavigate={() => { onClose(); navigateDirectory("organizations", o.id); }}
                                            onUnlink={() => unlinkOrganization(o.id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <Separator className="my-6" />

                        {/* Claims Section */}
                        <div>
                            <div className="flex items-center justify-between flex-wrap mb-3">
                                <h4 className="text-sm font-semibold flex items-center gap-2">
                                    <FileText className="h-4 w-4" /> Claims ({propertyClaims.length})
                                </h4>
                                <LinkEntityPopover
                                    existingIds={propertyClaims.map((cp) => cp.claimId)}
                                    onSearch={searchClaims}
                                    onSelect={(id) => linkClaim(id)}
                                    placeholder="Search claims..."
                                    emptyMessage="No matching claims"
                                    noItemsMessage="No claims yet"
                                    icon={FileText}
                                />
                            </div>
                            {claimsLoading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-8 w-full" />
                                </div>
                            ) : propertyClaims.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No linked claims</p>
                            ) : (
                                <div className="space-y-2">
                                    {propertyClaims.map((cp) => (
                                        <LinkedEntityRow
                                            key={cp.id}
                                            icon={FileText}
                                            label={[cp.insuredName, cp.claimNumber].filter(Boolean).join(" – ") || `Claim #${cp.claimId}`}
                                            onNavigate={() => { onClose(); setLocation(`/claims/${cp.claimId}`); }}
                                            onUnlink={() => unlinkClaim(cp.claimId)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}